import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import Modal from './Modal';
import { cleanProperName } from '../../utils/nameCleaning';
import { CASE_TYPE_OPTIONS, CASE_STATUS_OPTIONS, getStagesForType } from '../../utils/caseOptions';
import {
  fetchCourts, createCourt,
  fetchJudges, createJudge,
  fetchOpposingCounsels, createOpposingCounsel,
} from '../../api';

const toLookupOption = (row) => (row ? { value: row.id, label: row.name } : null);

/**
 * Standalone "Edit Case" form. Covers the case's own core fields (title,
 * description, type/status/stage, dates, court/judge/opposing-counsel,
 * parties involved). Deliberately does NOT cover client reassignment or
 * partners/associates/paralegals team editing - those stay
 * CaseManagement.js-only for now (team assignment already has its own
 * dedicated `assign-team` endpoint/UI; this modal only PATCHes the case's
 * own fields, which the backend's partial-update path leaves the
 * team M2M fields untouched for when omitted from the payload).
 * Also does not replicate CaseManagement.js's proactive conflict-of-interest
 * pre-flight check/resolution modal - a conflict on save surfaces as a
 * plain error toast instead of the guided resolution flow.
 */
export default function CaseEditModal({ open, onClose, onSaved, authAxios, showFormMessage, caseData }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [courts, setCourts] = useState([]);
  const [judges, setJudges] = useState([]);
  const [opposingCounsels, setOpposingCounsels] = useState([]);

  useEffect(() => {
    if (!open || !caseData) return;
    setForm({
      title: caseData.title || '',
      description: caseData.description || '',
      case_type: caseData.case_type || '',
      status: caseData.status || '',
      case_stage: caseData.case_stage || '',
      date_opened: caseData.date_opened || '',
      date_closed: caseData.date_closed || '',
      court: caseData.court || null,
      judge: caseData.judge || null,
      opposing_counsel_ref: caseData.opposing_counsel_ref || null,
      opposing_counsel: caseData.opposing_counsel || '',
      court_case_number: caseData.court_case_number || '',
      parties_involved: Array.isArray(caseData.parties_involved) ? caseData.parties_involved.join(', ') : (caseData.parties_involved || ''),
    });
  }, [open, caseData]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const [c, j, oc] = await Promise.all([
        fetchCourts(authAxios).catch(() => []),
        fetchJudges(authAxios).catch(() => []),
        fetchOpposingCounsels(authAxios).catch(() => []),
      ]);
      if (cancelled) return;
      setCourts(Array.isArray(c) ? c : []);
      setJudges(Array.isArray(j) ? j : []);
      setOpposingCounsels(Array.isArray(oc) ? oc : []);
    })();
    return () => { cancelled = true; };
  }, [open, authAxios]);

  const courtOptions = useMemo(() => courts.map(toLookupOption), [courts]);
  const judgeOptions = useMemo(() => judges.map(toLookupOption), [judges]);
  const opposingCounselOptions = useMemo(() => opposingCounsels.map(toLookupOption), [opposingCounsels]);
  const stageOptions = useMemo(() => getStagesForType(form?.case_type), [form?.case_type]);

  const handleCreateLookup = useCallback(async (kind, rawName, onPick) => {
    const cleaned = cleanProperName(rawName);
    if (!cleaned) return;
    try {
      let created;
      if (kind === 'court') {
        created = await createCourt(authAxios, { name: cleaned, court_type: 'OTHER', is_active: true });
        setCourts(prev => prev.some(c => c.id === created.id) ? prev : [...prev, created]);
      } else if (kind === 'judge') {
        created = await createJudge(authAxios, { name: cleaned, is_active: true });
        setJudges(prev => prev.some(j => j.id === created.id) ? prev : [...prev, created]);
      } else if (kind === 'opposing_counsel') {
        created = await createOpposingCounsel(authAxios, { name: cleaned, is_active: true });
        setOpposingCounsels(prev => prev.some(o => o.id === created.id) ? prev : [...prev, created]);
      }
      if (created && onPick) onPick(created.id);
    } catch (err) {
      showFormMessage?.('danger', `Could not save new ${kind.replace('_', ' ')} — please try again.`);
    }
  }, [authAxios, showFormMessage]);

  const fld = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        date_closed: form.date_closed || null,
        court: form.court || null,
        judge: form.judge || null,
        opposing_counsel_ref: form.opposing_counsel_ref || null,
        parties_involved: form.parties_involved
          ? form.parties_involved.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      await authAxios.patch(`cases/${caseData.id}/`, payload);
      showFormMessage?.('success', 'Case updated successfully!');
      onSaved?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data ? JSON.stringify(err.response.data) : (err?.message || 'Failed to update case.');
      showFormMessage?.('danger', `Failed to update case. ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [authAxios, form, caseData, showFormMessage, onSaved, onClose]);

  if (!form) return open ? null : null;

  return (
    <Modal open={open} onClose={onClose} title={`Edit Case: ${caseData?.title || ''}`} maxWidth="max-w-2xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="title" value={form.title} onChange={fld} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Case Type</label>
            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="case_type" value={form.case_type} onChange={fld}>
              {CASE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Case Stage</label>
            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="case_stage" value={form.case_stage} onChange={fld}>
              {stageOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="status" value={form.status} onChange={fld}>
              {CASE_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Opening Date</label>
            <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="date_opened" value={form.date_opened} onChange={fld} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Closing Date (optional)</label>
            <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="date_closed" value={form.date_closed || ''} onChange={fld} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Court (optional)</label>
            <CreatableSelect
              isClearable classNamePrefix="rs" placeholder="— Select or type a court —"
              formatCreateLabel={(input) => `Add court "${cleanProperName(input)}"`}
              options={courtOptions}
              value={courtOptions.find(o => o.value === form.court) || null}
              onChange={(opt) => setForm(f => ({ ...f, court: opt ? opt.value : null }))}
              onCreateOption={(input) => handleCreateLookup('court', input, (id) => setForm(f => ({ ...f, court: id })))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Judge (optional)</label>
            <CreatableSelect
              isClearable classNamePrefix="rs" placeholder="— Select or type a judge —"
              formatCreateLabel={(input) => `Add judge "${cleanProperName(input)}"`}
              options={judgeOptions}
              value={judgeOptions.find(o => o.value === form.judge) || null}
              onChange={(opt) => setForm(f => ({ ...f, judge: opt ? opt.value : null }))}
              onCreateOption={(input) => handleCreateLookup('judge', input, (id) => setForm(f => ({ ...f, judge: id })))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Court Case Number (optional)</label>
            <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="court_case_number" value={form.court_case_number} onChange={fld} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Opposing Counsel (optional)</label>
            <CreatableSelect
              isClearable classNamePrefix="rs" placeholder="— Select or type opposing counsel —"
              formatCreateLabel={(input) => `Add opposing counsel "${cleanProperName(input)}"`}
              options={opposingCounselOptions}
              value={opposingCounselOptions.find(o => o.value === form.opposing_counsel_ref) || null}
              onChange={(opt) => setForm(f => ({
                ...f, opposing_counsel_ref: opt ? opt.value : null, opposing_counsel: opt ? opt.label : '',
              }))}
              onCreateOption={(input) => handleCreateLookup('opposing_counsel', input, (id) => {
                const cleaned = cleanProperName(input);
                setForm(f => ({ ...f, opposing_counsel_ref: id, opposing_counsel: cleaned }));
              })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm" rows={3}
              name="description" value={form.description} onChange={fld} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Parties Involved (comma-separated)</label>
            <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm" rows={2}
              name="parties_involved" value={form.parties_involved} onChange={fld} />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
